import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import CardActionArea from '@mui/material/CardActionArea'
import Divider from '@mui/material/Divider'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import articles from '../data/articles.json'
import artikelImages from '../assets/artikelImages'

const ArtikelDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const article = articles.find(a => a.slug === slug)
  const related = articles.filter(a => a.slug !== slug).slice(0, 3)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  if (!article) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', pt: 10 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 700 }}>Artikel tidak ditemukan</Typography>
          <Button variant="contained" onClick={() => navigate('/artikel')} sx={{ bgcolor: '#2563eb', borderRadius: 99 }}>
            Kembali ke Daftar Artikel
          </Button>
        </Stack>
      </Box>
    )
  }

  const renderKonten = (block, idx) => {
    switch (block.tipe) {
      case 'paragraf':
        return (
          <Typography key={idx} sx={{ fontSize: '1.05rem', lineHeight: 1.85, color: '#374151', mb: 2.5 }}>
            {block.isi}
          </Typography>
        )
      case 'heading':
        return (
          <Typography key={idx} sx={{ fontSize: { xs: '1.3rem', md: '1.55rem' }, fontWeight: 800, color: '#0f172a', mt: 4.5, mb: 1.5, lineHeight: 1.3 }}>
            {block.isi}
          </Typography>
        )
      case 'subheading':
        return (
          <Typography key={idx} sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2563eb', mt: 2.5, mb: 1 }}>
            {block.isi}
          </Typography>
        )
      case 'list':
        return (
          <Box key={idx} component="ul" sx={{ pl: 0, mb: 2.5, listStyle: 'none' }}>
            {block.items.map((item, i) => (
              <Box
                key={i}
                component="li"
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  mb: 1.5, fontSize: '1.02rem', lineHeight: 1.8, color: '#374151',
                }}
              >
                <Box
                  sx={{
                    minWidth: 8, height: 8, borderRadius: '50%',
                    bgcolor: article.kategoriColor, mt: '10px', flexShrink: 0,
                  }}
                />
                {item}
              </Box>
            ))}
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: [
            'radial-gradient(ellipse at 70% 20%, rgba(37,99,235,0.15) 0%, transparent 55%)',
            'radial-gradient(ellipse at 20% 80%, rgba(249,115,22,0.1) 0%, transparent 50%)',
            '#0f172a',
          ].join(', '),
          pt: { xs: 14, md: 16 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/artikel')}
            sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }, borderRadius: 99, px: 2 }}
          >
            Semua Artikel
          </Button>
          <Chip
            label={article.kategori}
            size="small"
            sx={{ mb: 2.5, bgcolor: article.kategoriColor, color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}
          />
          <Typography
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.4rem', md: '2.9rem' },
              fontWeight: 900, lineHeight: 1.15, color: '#fff', mb: 3,
            }}
          >
            {article.judul}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap" gap={1.5}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <PersonIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>{article.penulis}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>{article.tanggal}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>{article.readTime}</Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Article Image + Content */}
      <Container maxWidth="md" sx={{ mt: { xs: -2, md: -4 }, position: 'relative', zIndex: 2 }}>
        {/* Cover Image */}
        <Box
          component="img"
          src={artikelImages[article.gambar]}
          alt={article.judul}
          sx={{
            width: '100%', height: { xs: 220, sm: 320, md: 440 },
            objectFit: 'cover', borderRadius: 3,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          }}
        />

        {/* Summary box */}
        <Box
          sx={{
            mt: 4, p: { xs: 2.5, md: 3.5 },
            bgcolor: '#fff', borderRadius: 3, border: `3px solid ${article.kategoriColor}`,
            borderLeft: `6px solid ${article.kategoriColor}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}
        >
          <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#374151', fontStyle: 'italic', fontWeight: 500 }}>
            {article.ringkasan}
          </Typography>
        </Box>

        {/* Article Body */}
        <Box sx={{ mt: 5, bgcolor: '#fff', borderRadius: 3, p: { xs: 3, md: 5 }, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
          {article.konten.map((block, idx) => renderKonten(block, idx))}
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Related Articles */}
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', mb: 3 }}>
          Artikel Terkait
        </Typography>
        <Grid container spacing={2.5}>
          {related.map(rel => (
            <Grid item xs={12} sm={4} key={rel.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0',
                  transition: 'all 0.3s ease',
                  '&:hover': { boxShadow: '0 12px 36px rgba(0,0,0,0.1)', transform: 'translateY(-3px)' },
                }}
              >
                <CardActionArea onClick={() => navigate(`/artikel/${rel.slug}`)}>
                  <CardMedia
                    component="img"
                    image={artikelImages[rel.gambar]}
                    alt={rel.judul}
                    sx={{ height: 150, objectFit: 'cover' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Chip
                      label={rel.kategori}
                      size="small"
                      sx={{ mb: 1, bgcolor: rel.kategoriColor, color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}
                    />
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                      {rel.judul}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Back button */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate('/artikel')}
            sx={{ borderRadius: 99, px: 3.5, py: 1.2, borderColor: '#2563eb', color: '#2563eb', fontWeight: 700, '&:hover': { bgcolor: '#2563eb', color: '#fff' } }}
          >
            Kembali ke Semua Artikel
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default ArtikelDetail
